/**
 * Professions (อาชีพ, docs/systems/PROFESSIONS.md): gathering at resource nodes, planting and harvesting
 * plots, crafting recipes, and using or trading the goods. The World owns one `Trades` and hands it
 * the commands and the clock; everything here is deterministic (the world's Rng, `step` time only).
 *
 * Working shows on the player through the emote channel: a job sets `emote` to `work-<pose>` (the
 * townsfolk work poses), so the weapon goes to its scabbard and moving, being struck or a new emote
 * ends it, exactly as with a ท่าโพส.
 */
import { BAG_SIZE, ITEMS } from './data/items';
import { combatBlockText } from './data/combat';
import {
  BIG_PLAINS_NODES,
  BIG_PLAINS_ONLY,
  CRAFT_BATCH_MAX,
  CROPS,
  GOOD_COOLDOWN,
  GOOD_STACK,
  GOODS,
  isProfessionId,
  NODE_KINDS,
  NODE_REACH,
  NODE_SPOT_IDS,
  NODE_SPOTS,
  PLANT_TIME,
  PROFESSIONS,
  RECIPES,
  TRADE_MAX_LEVEL,
  shownPose,
  tradeXpToNext,
  type CropDef,
  type GoodCount,
  type NodeKindDef,
  type NodeTerrain,
  type ProfessionId,
  type RecipeDef,
  type WorkPose,
  type Yield,
} from './data/professions';
import {
  FARM,
  isToolId,
  isToolSlot,
  NODE_TOOL,
  NO_TOOL,
  TOOL_FX,
  TOOL_SLOTS,
  TOOLS,
  toolOf,
  type ToolSlotId,
} from './data/tools';
import { screenFacing, screenHeading } from './iso';
import { isBorder, isOpen, Tile, tileAt, type GameMap, type Point } from './map';
import { freeAt } from './path';
import { randInt, type Rng } from './rng';
import type { PlayerProgress } from './progress';
import type { Entity, WorldEvent } from './world';

// ───────────────────────── saved state ─────────────────────────

/** A planted plot: the seed sown and the seconds it has grown. */
export interface PlotState {
  crop: string;
  /** Growth done, in seconds of full-speed growth (a dry plot earns less of it per second). */
  grown: number;
  /** Seconds of watering left (a plot that is dry grows slower, never dies). */
  watered?: number;
  /** Manured: grows faster and gives more. */
  manure?: boolean;
}

/** Everything about the trades that is saved (`PlayerProgress.trades`). */
export interface TradeState {
  /** Trade xp by profession (the level is worked out from it). */
  xp: Partial<Record<ProfessionId, number>>;
  /** Goods carried, by id (kept apart from the equipment bag). */
  goods: Record<string, number>;
  /** Planted plots by node id. */
  plots: Record<string, PlotState>;
  /** The tool belt: tool slot -> tool id (data/tools.ts); not bag items, never dropped. */
  tools: Record<string, string>;
}

export const newTrades = (): TradeState => ({ xp: {}, goods: {}, plots: {}, tools: {} });

const count = (v: unknown, max = Infinity): number =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.min(max, v) : 0;

/** A saved TradeState repaired against current data (saves from before professions get an empty one). */
export function parseTrades(v: unknown): TradeState {
  const t = newTrades();
  if (!v || typeof v !== 'object') return t;
  const raw = v as Record<string, unknown>;
  const rec = (x: unknown) => (x && typeof x === 'object' ? (x as Record<string, unknown>) : {});
  for (const [id, xp] of Object.entries(rec(raw.xp)))
    if (isProfessionId(id) && count(xp)) t.xp[id] = count(xp);
  for (const [id, n] of Object.entries(rec(raw.goods)))
    if (GOODS[id] && count(n) >= 1) t.goods[id] = Math.floor(count(n, GOOD_STACK));
  for (const [id, p] of Object.entries(rec(raw.plots))) {
    const plot = rec(p);
    const crop = typeof plot.crop === 'string' ? CROPS[plot.crop] : undefined;
    if (crop && NODE_SPOT_IDS.has(id)) {
      const pl: PlotState = { crop: crop.seed, grown: count(plot.grown, crop.grow) };
      if (count(plot.watered)) pl.watered = count(plot.watered, FARM.waterSeconds * 4);
      if (plot.manure === true) pl.manure = true;
      t.plots[id] = pl;
    }
  }
  for (const [slot, id] of Object.entries(rec(raw.tools)))
    if (isToolSlot(slot) && isToolId(id) && TOOLS[id as string]!.slot === slot) t.tools[slot] = id as string;
  return t;
}

// ───────────────────────── rules ─────────────────────────

/** A trade's level for its xp, the xp into that level and what the next one needs (0 at the top). */
export function tradeLevel(xp: number): { level: number; into: number; need: number } {
  let level = 1;
  let left = Math.max(0, xp);
  while (level < TRADE_MAX_LEVEL && left >= tradeXpToNext(level)) {
    left -= tradeXpToNext(level);
    level++;
  }
  return {
    level,
    into: level < TRADE_MAX_LEVEL ? left : 0,
    need: level < TRADE_MAX_LEVEL ? tradeXpToNext(level) : 0,
  };
}

/** Seconds a gather takes: 6% quicker for each level over the node's, never under half. */
export const gatherSeconds = (base: number, level: number, need: number): number =>
  base * Math.max(0.5, 1 - 0.06 * Math.max(0, level - need));

/** Chance of one more of each yield: 6% for each level over what is needed, at most 40%. */
export const bonusChance = (level: number, need: number): number =>
  Math.min(0.4, 0.06 * Math.max(0, level - need));

/** Rolls what a gather or harvest gives at a trade level. */
export function rollYields(
  yields: readonly Yield[],
  level: number,
  need: number,
  rng: Rng,
  /** The tool and the day's extras (data/tools.ts): `bonus` chance added, `cut` the share a bare-handed gather gives, `rare` times the chance of a rare yield (bait). */
  x: { bonus?: number; cut?: number; rare?: number } = {},
): GoodCount[] {
  const out: GoodCount[] = [];
  const bonus = Math.min(0.6, bonusChance(level, need) + (x.bonus ?? 0));
  for (const y of yields) {
    if (level < (y.lv ?? need)) continue;
    const chance = y.chance < 1 && x.rare ? Math.min(1, y.chance * x.rare) : y.chance;
    if (rng() >= chance) continue;
    let n = randInt(rng, y.min, y.max) + (rng() < bonus ? 1 : 0);
    if (x.cut !== undefined && x.cut < 1) n = Math.max(1, Math.floor(n * x.cut));
    if (n > 0) out.push({ id: y.id, n });
  }
  return out;
}

/** Plots grow on game time, wherever the player is (and while away, through the client). */
export function growPlots(t: TradeState, seconds: number): void {
  if (!(seconds > 0)) return;
  for (const p of Object.values(t.plots)) {
    const crop = CROPS[p.crop];
    if (!crop) continue;
    // Wet seconds grow at full speed, dry ones at `FARM.dryRate`; manure makes it all faster. Nothing ever dies of thirst.
    const wet = Math.min(p.watered ?? 0, seconds);
    const rate = p.manure ? FARM.manureGrow : 1;
    p.grown = Math.min(crop.grow, p.grown + (wet + (seconds - wet) * FARM.dryRate) * rate);
    if (wet > 0) {
      p.watered = (p.watered ?? 0) - wet;
      if (p.watered <= 1e-9) delete p.watered;
    }
  }
}

export const plotRipe = (p: PlotState | undefined): boolean =>
  !!p && p.grown >= (CROPS[p.crop]?.grow ?? Infinity);

/** The emote id a work pose is shown with (render/emotes.ts draws `work-*`). */
export const WORK_EMOTE_PREFIX = 'work-';
export const workEmote = (pose: WorkPose): string => `${WORK_EMOTE_PREFIX}${shownPose(pose)}`;

// ───────────────────────── nodes on the map ─────────────────────────

/** A node placed on this zone's map: where to stand, the feature worked (a tree, the water), its uses. */
export interface PlacedNode {
  readonly id: string;
  readonly kind: NodeKindDef;
  readonly x: number;
  readonly y: number;
  /** The tile worked (the tree, the rock face, the water), to face. */
  readonly fx: number;
  readonly fy: number;
  /** Gathers left before it is spent, and seconds until a spent node comes back. */
  left: number;
  regrowT: number;
}

const N4: readonly [number, number][] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];
const N8: readonly [number, number][] = [...N4, [1, 1], [1, -1], [-1, 1], [-1, -1]];

/** The tile beside (i, j) a node of this terrain works, or null when (i, j) does not fit it. */
function featureOf(m: GameMap, terrain: NodeTerrain, i: number, j: number): [number, number] | null {
  const t = tileAt(m, i, j);
  if (!isOpen(t) || !freeAt(m, i + 0.5, j + 0.5)) return null;
  const next = (dirs: readonly [number, number][], ok: (n: number, a: number, b: number) => boolean) => {
    for (const [di, dj] of dirs)
      if (ok(tileAt(m, i + di, j + dj), i + di, j + dj)) return [i + di, j + dj] as [number, number];
    return null;
  };
  switch (terrain) {
    case 'tree':
      return t === Tile.Floor ? next(N4, (n) => n === Tile.Tree) : null;
    case 'herb':
      return t === Tile.Floor ? next(N8, (n) => n === Tile.Tree) && [i, j] : null;
    case 'rock':
      return t === Tile.Floor ? next(N4, (n, a, b) => n === Tile.Rock && !isBorder(m, a, b)) : null;
    case 'water':
      return t === Tile.Floor || t === Tile.Pier ? next(N4, (n) => n === Tile.Water) : null;
    case 'field':
      return t === Tile.Paddy ? [i, j] : null;
    case 'open':
      return t !== Tile.Paddy ? [i, j] : null;
  }
}

/** How far from its named spot a node may be moved to find ground that fits it. */
const PLACE_RADIUS = 18;

/**
 * Places this zone's nodes (data/professions NODE_SPOTS): each at the nearest tile to its spot that
 * fits it, two nodes never side by side. Depends on the map only, so it is the same every load.
 */
export function placeNodes(m: GameMap): PlacedNode[] {
  const taken = new Set<number>();
  const out: PlacedNode[] = [];
  for (const s of NODE_SPOTS) {
    if (s.zone !== m.zone) continue;
    if (m.zone === 'plains' && m.w <= 128 && BIG_PLAINS_ONLY.has(s.id)) continue;
    const kind = NODE_KINDS[s.kind];
    const base: Point | undefined = s.at === 'plaza' ? m.plaza : m.landmarks?.[s.at];
    if (!kind || !base) continue;
    // The 256 m plains put their nodes out past the town (data/professions BIG_PLAINS_NODES).
    const [dx, dy] = (m.zone === 'plains' && m.w > 128 && BIG_PLAINS_NODES[s.id]) || [s.dx, s.dy];
    const cx = base.x + dx;
    const cy = base.y + dy;
    const ci = Math.floor(cx);
    const cj = Math.floor(cy);
    let best: { i: number; j: number; f: [number, number]; d: number } | null = null;
    for (let r = 0; r <= PLACE_RADIUS && !best; r++)
      for (let j = cj - r; j <= cj + r; j++)
        for (let i = ci - r; i <= ci + r; i++) {
          if (Math.max(Math.abs(i - ci), Math.abs(j - cj)) !== r || taken.has(j * m.w + i)) continue;
          const f = featureOf(m, kind.terrain, i, j);
          if (!f) continue;
          const d = Math.hypot(i + 0.5 - cx, j + 0.5 - cy);
          if (!best || d < best.d) best = { i, j, f, d };
        }
    if (!best) continue;
    for (const [di, dj] of [[0, 0], ...N8] as [number, number][])
      taken.add((best.j + dj) * m.w + best.i + di);
    out.push({
      id: s.id,
      kind,
      x: best.i + 0.5,
      y: best.j + 0.5,
      fx: best.f[0] + 0.5,
      fy: best.f[1] + 0.5,
      left: kind.uses,
      regrowT: 0,
    });
  }
  return out;
}

// ───────────────────────── commands and events ─────────────────────────

/** Player commands of the trades (part of `PlayerCommand`). */
export type TradeCommand =
  /** Work a node: gather it, or harvest a ripe plot. Keeps on while the node has gathers left. */
  | { t: 'gather'; node: string }
  /** Sow a seed in an empty plot. */
  | { t: 'plant'; node: string; seed: string }
  /** Water a planted plot (the watering can; by hand without one, less). */
  | { t: 'water'; node: string }
  /** Spread a manure on a planted plot. */
  | { t: 'fertilize'; node: string }
  /** Buy a tier 1 tool from the trade master (the merchant) for the tool belt. */
  | { t: 'buyTool'; id: string }
  /** Make a recipe `count` times in a row (1 if left out). */
  | { t: 'craft'; recipe: string; count?: number }
  /** Put down the work under way. */
  | { t: 'workStop' }
  /** Eat a meal or take a medicine. */
  | { t: 'useGood'; id: string }
  /** Buy goods from the merchant (seeds, silk thread), or sell him any good. */
  | { t: 'buyGood'; id: string; n?: number }
  | { t: 'sellGood'; id: string; n?: number };

export type JobKind = 'gather' | 'plant' | 'craft' | 'water' | 'fertilize';
export type StopReason = 'moved' | 'hit' | 'stopped' | 'dead' | 'short' | 'full';

/** World events of the trades (part of `WorldEvent`). */
export type TradeEvent =
  /** Work began, finished (nothing more to do) or was broken off (`reason`). */
  | {
      type: 'work';
      id: number;
      phase: 'start' | 'done' | 'stop';
      job: JobKind;
      prof: ProfessionId;
      reason?: StopReason;
    }
  /** A gather or harvest landed: what it gave. */
  | { type: 'gathered'; node: string; prof: ProfessionId; goods: GoodCount[] }
  /** One of a recipe was made. */
  | { type: 'crafted'; recipe: string; prof: ProfessionId }
  /** A trade rose a level. */
  | { type: 'tradeLevel'; prof: ProfessionId; level: number }
  /** A meal or medicine was used. */
  | { type: 'usedGood'; id: number; good: string };

/** The work under way. */
export interface Job {
  readonly kind: JobKind;
  readonly prof: ProfessionId;
  readonly pose: WorkPose;
  /** The node worked (gather, plant) or the recipe made (craft), and the seed sown. */
  readonly node?: string;
  readonly recipe?: string;
  readonly seed?: string;
  /** Seconds into the current piece of work, and how long it takes. */
  t: number;
  total: number;
  /** Crafts still to make after this one. */
  more: number;
  /** HP when last looked at: losing any breaks the work off. */
  hp: number;
}

/** What the World lends the trades. */
export interface TradeHost {
  readonly map: GameMap;
  readonly progress: PlayerProgress;
  readonly player: Entity;
  readonly rng: Rng;
  emit(e: WorldEvent): void;
  notice(text: string): void;
  log(text: string): void;
  addStatus(e: Entity, id: string, stacks: number): void;
  /** Near the merchant (with a notice when not). */
  nearMerchant(): boolean;
  /** The player may not work now (an arena bout). */
  busy(): boolean;
  /** Fills the training-boost ledger's item slot (systems/cultivation.ts). */
  trainBoost?(bonus: number, seconds: number): void;
}

const fmtGoods = (gs: readonly GoodCount[]) =>
  gs.map((g) => `${GOODS[g.id]?.name ?? g.id} ×${g.n}`).join(' ');

/** The trades in one World: its nodes, the work under way, and the commands. */
export class Trades {
  readonly nodes: PlacedNode[];
  job: Job | null = null;
  /** Seconds until another meal or medicine may be used. */
  goodCooldown = 0;

  constructor(private readonly host: TradeHost) {
    this.nodes = placeNodes(host.map);
  }

  private get state(): TradeState {
    return this.host.progress.trades;
  }

  level(prof: ProfessionId): number {
    return tradeLevel(this.state.xp[prof] ?? 0).level;
  }

  has(id: string): number {
    return this.state.goods[id] ?? 0;
  }

  /** The belt's tool in a slot, or undefined. */
  tool(slot: ToolSlotId) {
    return toolOf(this.state.tools, slot);
  }

  /** What the tool in a slot does: its tier's numbers, or the bare-handed ones when the slot is empty. */
  fx(slot: ToolSlotId): { time: number; bonus: number; water: number; seedSave: number; has: boolean } {
    const t = this.tool(slot);
    return t ? { ...TOOL_FX[t.tier], has: true } : { ...NO_TOOL, has: false };
  }

  /** The tool slot a crafting trade works with (its hammer, scissors, wok), if it has one. */
  private craftSlot(prof: ProfessionId): ToolSlotId | null {
    return (Object.values(TOOL_SLOTS).find((s) => s.prof === prof && (s.id === 'hammer' || s.id === 'scissors' || s.id === 'wok'))?.id) ?? null;
  }

  node(id: string): PlacedNode | undefined {
    return this.nodes.find((n) => n.id === id);
  }

  /** The nearest node within reach of a point, or null. */
  nodeNear(p: Point, reach = NODE_REACH): PlacedNode | null {
    let best: PlacedNode | null = null;
    let bd = reach;
    for (const n of this.nodes) {
      const d = Math.hypot(n.x - p.x, n.y - p.y);
      if (d <= bd) [best, bd] = [n, d];
    }
    return best;
  }

  /** Whether the player stands by a station node of this kind (a mill), within twice a node's reach. */
  stationNear(kind: string): boolean {
    const p = this.host.player;
    return this.nodes.some((n) => n.kind.id === kind && Math.hypot(n.x - p.x, n.y - p.y) <= NODE_REACH * 2);
  }

  /** A plot's crop, when planted. */
  plot(id: string): PlotState | undefined {
    return this.state.plots[id];
  }

  /** The seeds the player could sow now (owned, and farming level enough), best first. */
  seedsToPlant(): CropDef[] {
    const lv = this.level('farmer');
    return Object.values(CROPS)
      .filter((c) => c.level <= lv && this.has(c.seed) > 0)
      .reverse();
  }

  /** Whether a recipe can be made now, or why not. */
  canCraft(r: RecipeDef): string | null {
    if (this.level(r.prof) < r.level) return `ต้องมี${PROFESSIONS[r.prof].name} ขั้น ${r.level}`;
    if (r.station && !this.stationNear(r.station)) return `ต้องทำข้าง${NODE_KINDS[r.station]?.name ?? 'ที่ทำงาน'}`;
    const short = r.inputs.find((i) => this.has(i.id) < i.n);
    if (short) return `${GOODS[short.id]?.name ?? short.id}ไม่พอ`;
    if ('item' in r.out && this.host.progress.inventory.length >= BAG_SIZE) return 'กระเป๋าเต็ม';
    if ('tool' in r.out) {
      const t = TOOLS[r.out.tool];
      if (!t) return 'ไม่มีเครื่องมือนี้';
      const have = this.tool(t.slot)?.tier ?? 0;
      if (have >= t.tier) return 'มีเครื่องมือขั้นนี้หรือดีกว่าแล้ว';
      if (have < t.tier - 1) return 'ต้องมีเครื่องมือขั้นก่อนหน้าก่อน';
    }
    return null;
  }

  apply(cmd: TradeCommand): void {
    switch (cmd.t) {
      case 'gather':
        return this.gather(cmd.node);
      case 'plant':
        return this.plant(cmd.node, cmd.seed);
      case 'water':
        return this.water(cmd.node);
      case 'fertilize':
        return this.fertilize(cmd.node);
      case 'buyTool':
        return this.buyTool(cmd.id);
      case 'craft':
        return this.craft(cmd.recipe, cmd.count ?? 1);
      case 'workStop':
        if (this.job) this.stop('stopped');
        return;
      case 'useGood':
        return this.use(cmd.id);
      case 'buyGood':
        return this.buy(cmd.id, cmd.n ?? 1);
      case 'sellGood':
        return this.sell(cmd.id, cmd.n ?? 1);
    }
  }

  step(dt: number): void {
    this.goodCooldown = Math.max(0, this.goodCooldown - dt);
    growPlots(this.state, dt);
    for (const n of this.nodes)
      if (n.left <= 0 && (n.regrowT -= dt) <= 0) {
        n.left = n.kind.uses;
        n.regrowT = 0;
      }
    const j = this.job;
    if (!j) return;
    const why = this.interrupted(j);
    if (why) return this.stop(why);
    j.t += dt;
    if (j.t >= j.total) this.finish(j);
  }

  // ── starting work ──

  /** Why the player cannot start work now (with the notice to show), or null. */
  private cannotWork(): string | null {
    const p = this.host.player;
    if (this.host.busy()) return 'อยู่ในลานประลอง';
    if (p.dead) return 'ตายอยู่';
    if (p.mount) return 'ลงจากพาหนะก่อนจึงทำงานได้';
    if (p.combatT > 0) return combatBlockText(p);
    if (p.moving || p.z > 0.5 || p.cast || p.blocking || p.stunT > 0 || p.dashT > 0 || p.swingT > 0)
      return 'ต้องยืนนิ่งก่อนจึงทำงานได้';
    if (p.meditating) return 'กำลังนั่งสมาธิ';
    return null;
  }

  private begin(job: Omit<Job, 't' | 'hp'>, face?: Point): void {
    const p = this.host.player;
    const why = this.cannotWork();
    if (why) return this.host.notice(why);
    if (this.job) this.stop('stopped');
    this.job = { ...job, t: 0, hp: p.hp };
    p.path = null;
    if (face) {
      // Turned to the tree, the rock face, the water.
      const sf = screenFacing(face.x - p.x, face.y - p.y);
      if (Math.abs(sf) > 0.01) p.facing = sf > 0 ? 1 : -1;
      if (Math.hypot(face.x - p.x, face.y - p.y) > 0.01)
        p.heading = screenHeading(face.x - p.x, face.y - p.y);
    }
    this.pose(workEmote(job.pose));
    this.host.emit({ type: 'work', id: p.id, phase: 'start', job: job.kind, prof: job.prof });
  }

  /** Shows (or clears) the work pose on the emote channel. */
  private pose(emote: string | null): void {
    const p = this.host.player;
    if (p.emote === emote) return;
    p.emote = emote;
    p.emoteT = 0;
    this.host.emit({ type: 'emote', id: p.id, emote });
  }

  /** The node if the player stands close enough to it, else a notice. */
  private reach(id: string): PlacedNode | null {
    const n = this.node(id);
    if (!n) return null;
    if (Math.hypot(n.x - this.host.player.x, n.y - this.host.player.y) > NODE_REACH + 0.2) {
      this.host.notice(`ต้องเข้าไปใกล้${n.kind.name}ก่อน`);
      return null;
    }
    return n;
  }

  private gather(id: string): void {
    const n = this.reach(id);
    if (!n) return;
    const k = n.kind;
    const lv = this.level(k.prof);
    if (k.station) return this.host.notice(`${k.verb}ได้ที่หน้าต่างอาชีพ (ยืนข้าง${k.name})`);
    if (k.id === 'plot') {
      const p = this.plot(n.id);
      if (!p) return this.host.notice('แปลงนี้ยังว่าง ต้องปลูกก่อน');
      if (!plotRipe(p)) return this.host.notice(`${CROPS[p.crop]?.name ?? ''}ยังไม่สุก`);
      const crop = CROPS[p.crop]!;
      return this.begin(
        {
          kind: 'gather',
          prof: k.prof,
          pose: k.pose,
          node: n.id,
          total: gatherSeconds(k.time, lv, crop.level) * this.fx('sickle').time,
          more: 0,
        },
        { x: n.fx, y: n.fy },
      );
    }
    if (lv < k.level) return this.host.notice(`ต้องมี${PROFESSIONS[k.prof].name} ขั้น ${k.level}`);
    const nt = NODE_TOOL[k.id];
    if (nt && nt.tier > (this.tool(nt.slot)?.tier ?? 0))
      return this.host.notice(`${k.name}ต้องใช้${TOOL_SLOTS[nt.slot].name}ขั้นเหล็กขึ้นไป`);
    if (n.left <= 0) return this.host.notice(`${k.name}หมดแล้ว รออีก ${Math.ceil(n.regrowT)} วินาที`);
    this.begin(
      {
        kind: 'gather',
        prof: k.prof,
        pose: k.pose,
        node: n.id,
        total: this.gatherTime(k, lv),
        more: 0,
      },
      { x: n.fx, y: n.fy },
    );
  }

  /** Seconds one gather at a node takes: the trade level's, then the tool's (slower with none). */
  private gatherTime(k: NodeKindDef, lv: number): number {
    const nt = NODE_TOOL[k.id];
    return gatherSeconds(k.time, lv, k.level) * (nt ? this.fx(nt.slot).time : 1);
  }

  private water(id: string): void {
    const n = this.reach(id);
    if (!n || n.kind.id !== 'plot') return;
    const p = this.plot(n.id);
    if (!p) return this.host.notice('แปลงนี้ยังว่าง ไม่ต้องรดน้ำ');
    if (plotRipe(p)) return this.host.notice(`${CROPS[p.crop]?.name ?? ''}สุกแล้ว เกี่ยวได้เลย`);
    const f = this.fx('can');
    this.begin(
      { kind: 'water', prof: 'farmer', pose: 'water', node: n.id, total: FARM.waterTime * (f.has ? f.time : NO_TOOL.waterTime), more: 0 },
      { x: n.fx, y: n.fy },
    );
  }

  private fertilize(id: string): void {
    const n = this.reach(id);
    if (!n || n.kind.id !== 'plot') return;
    const p = this.plot(n.id);
    if (!p) return this.host.notice('แปลงนี้ยังว่าง ต้องปลูกก่อน');
    if (plotRipe(p)) return this.host.notice(`${CROPS[p.crop]?.name ?? ''}สุกแล้ว เกี่ยวได้เลย`);
    if (p.manure) return this.host.notice('แปลงนี้ใส่ปุ๋ยแล้ว');
    if (this.has('manure') < 1) return this.host.notice('ไม่มีปุ๋ยคอก');
    this.begin(
      { kind: 'fertilize', prof: 'farmer', pose: 'sow', node: n.id, total: FARM.manureTime, more: 0 },
      { x: n.fx, y: n.fy },
    );
  }

  private buyTool(id: string): void {
    const t = TOOLS[id];
    if (!t || t.tier !== 1) return this.host.notice('ซื้อได้เฉพาะเครื่องมือขั้นไม้ ขั้นเหล็กให้ช่างตีเหล็กทำ');
    if (!this.host.nearMerchant()) return;
    const slot = TOOL_SLOTS[t.slot];
    if (this.tool(t.slot)) return this.host.notice(`มี${slot.name}อยู่แล้ว`);
    const pr = this.host.progress;
    if (pr.gold < slot.price) return this.host.notice('ตำลึงไม่พอ');
    pr.gold -= slot.price;
    this.state.tools[t.slot] = t.id;
    this.host.notice(`ซื้อ「${t.name}」แล้ว (ใส่ที่เข็มขัดเครื่องมือ)`);
    this.host.emit({ type: 'progressChanged' });
  }

  private plant(id: string, seed: string): void {
    const n = this.reach(id);
    const crop = CROPS[seed];
    if (!n || !crop || n.kind.id !== 'plot') return;
    if (this.plot(n.id)) return this.host.notice('แปลงนี้ปลูกไว้แล้ว');
    if (this.level('farmer') < crop.level) return this.host.notice(`ต้องมีชาวนา ขั้น ${crop.level}`);
    if (this.has(seed) < 1) return this.host.notice(`ไม่มี${GOODS[seed]?.name ?? 'เมล็ด'}`);
    this.begin(
      { kind: 'plant', prof: 'farmer', pose: 'sow', node: n.id, seed, total: PLANT_TIME * this.fx('hoe').time, more: 0 },
      { x: n.fx, y: n.fy },
    );
  }

  private craft(id: string, n: number): void {
    const r = RECIPES[id];
    if (!r) return;
    const why = this.canCraft(r);
    if (why) return this.host.notice(why);
    const times = Math.max(1, Math.min(CRAFT_BATCH_MAX, Math.floor(n) || 1));
    this.begin({
      kind: 'craft',
      prof: r.prof,
      pose: PROFESSIONS[r.prof].pose,
      recipe: r.id,
      total: this.craftTime(r),
      more: times - 1,
    });
  }

  /** Seconds one of a recipe takes: its own, then the trade's tool (slower with none; a tool's own making is not sped up). */
  private craftTime(r: RecipeDef): number {
    const slot = this.craftSlot(r.prof);
    return r.time * (slot && !('tool' in r.out) ? this.fx(slot).time : 1);
  }

  // ── the work under way ──

  private interrupted(j: Job): StopReason | null {
    const p = this.host.player;
    if (p.dead) return 'dead';
    if (p.hp < j.hp - 1e-6 || p.hitT > 0 || p.stunT > 0) return 'hit';
    j.hp = p.hp;
    if (p.moving || p.mount || p.z > 0.5 || p.dashT > 0) return 'moved';
    // Anything else that ended the pose (an emote, a strike, the stop button).
    if (p.emote !== workEmote(j.pose) || p.cast || p.swingT > 0 || p.blocking || this.host.busy())
      return 'stopped';
    return null;
  }

  private stop(reason: StopReason): void {
    const j = this.job;
    if (!j) return;
    this.job = null;
    const p = this.host.player;
    if (p.emote === workEmote(j.pose)) this.pose(null);
    const done = reason === 'short' || reason === 'full';
    this.host.emit({
      type: 'work',
      id: p.id,
      phase: done ? 'done' : 'stop',
      job: j.kind,
      prof: j.prof,
      reason,
    });
    if (reason === 'moved') this.host.notice('ขยับตัว งานถูกขัด');
    else if (reason === 'hit') this.host.notice('ถูกโจมตี งานถูกขัด');
  }

  /** The end of all work: the pose let go. */
  private done(j: Job): void {
    this.job = null;
    const p = this.host.player;
    if (p.emote === workEmote(j.pose)) this.pose(null);
    this.host.emit({ type: 'work', id: p.id, phase: 'done', job: j.kind, prof: j.prof });
  }

  private finish(j: Job): void {
    if (j.kind === 'craft') return this.finishCraft(j);
    const n = this.node(j.node ?? '');
    if (!n) return this.done(j);
    if (j.kind === 'water' || j.kind === 'fertilize') {
      const p = this.plot(n.id);
      if (p && !plotRipe(p)) {
        if (j.kind === 'water') {
          p.watered = FARM.waterSeconds * this.fx('can').water;
          this.host.log('รดน้ำแล้ว');
        } else if (this.take('manure', 1)) {
          p.manure = true;
          this.host.log('ใส่ปุ๋ยแล้ว');
        }
        this.gainXp('farmer', 1);
        this.host.emit({ type: 'progressChanged' });
      }
      return this.done(j);
    }
    if (j.kind === 'plant') {
      // The seed bag may save the seed.
      const save = this.fx('seedbag').seedSave;
      const saved = save > 0 && this.has(j.seed!) > 0 && this.host.rng() < save;
      if (saved || this.take(j.seed!, 1)) {
        this.state.plots[n.id] = { crop: j.seed!, grown: 0 };
        this.gainXp('farmer', 2);
        this.host.log(`ปลูก${CROPS[j.seed!]!.name}แล้ว`);
        this.host.emit({ type: 'progressChanged' });
      }
      return this.done(j);
    }
    // A harvest: the crop's yields, and the plot is empty again.
    if (n.kind.id === 'plot') {
      const p = this.plot(n.id);
      const crop = p && CROPS[p.crop];
      if (!crop || !plotRipe(p)) return this.done(j);
      delete this.state.plots[n.id];
      const f = this.fx('sickle');
      this.landYield(
        n,
        j.prof,
        rollYields(crop.yields, this.level('farmer'), crop.level, this.host.rng, {
          bonus: f.bonus + (p!.manure ? FARM.manureBonus : 0),
          cut: f.has ? 1 : NO_TOOL.yieldMul,
        }),
        crop.xp,
      );
      return this.done(j);
    }
    const k = n.kind;
    const nt = NODE_TOOL[k.id];
    const f = nt ? this.fx(nt.slot) : null;
    // Bait on the hook (one a cast): the rare catches come more often.
    const baited = k.id === 'fish-spot' && this.take('bait', 1);
    this.landYield(
      n,
      k.prof,
      rollYields(k.yields, this.level(k.prof), k.level, this.host.rng, {
        bonus: f?.bonus ?? 0,
        cut: !f || f.has ? 1 : NO_TOOL.yieldMul,
        ...(baited ? { rare: FARM.baitRare } : {}),
      }),
      k.xp,
    );
    n.left--;
    if (n.left <= 0) {
      n.regrowT = k.regrow;
      this.host.log(`${k.name}หมดแล้ว`);
      return this.done(j);
    }
    // Keep on at the same node.
    j.t = 0;
    j.total = this.gatherTime(k, this.level(k.prof));
  }

  private landYield(n: PlacedNode, prof: ProfessionId, goods: GoodCount[], xp: number): void {
    for (const g of goods) this.give(g.id, g.n);
    this.host.emit({ type: 'gathered', node: n.id, prof, goods });
    this.host.log(goods.length ? `${n.kind.verb}ได้ ${fmtGoods(goods)}` : `${n.kind.verb}ไม่ได้อะไร`);
    this.gainXp(prof, xp);
    this.host.emit({ type: 'progressChanged' });
  }

  private finishCraft(j: Job): void {
    const r = RECIPES[j.recipe ?? ''];
    if (!r) return this.done(j);
    const why = this.canCraft(r);
    if (why) {
      this.host.notice(why);
      return this.stop(why === 'กระเป๋าเต็ม' ? 'full' : 'short');
    }
    for (const i of r.inputs) this.take(i.id, i.n);
    let name: string;
    if ('item' in r.out) {
      this.host.progress.inventory.push(r.out.item);
      name = ITEMS[r.out.item]?.name ?? r.out.item;
    } else if ('tool' in r.out) {
      const t = TOOLS[r.out.tool]!;
      this.state.tools[t.slot] = t.id;
      name = t.name;
    } else {
      this.give(r.out.good, r.out.n);
      name = `${GOODS[r.out.good]?.name ?? r.out.good} ×${r.out.n}`;
    }
    this.host.emit({ type: 'crafted', recipe: r.id, prof: r.prof });
    this.host.log(`${PROFESSIONS[r.prof].name}: ทำ「${name}」สำเร็จ`);
    this.gainXp(r.prof, r.xp);
    this.host.emit({ type: 'progressChanged' });
    if (j.more <= 0) return this.done(j);
    j.more--;
    j.t = 0;
    const next = this.canCraft(r);
    if (next) {
      this.host.notice(next);
      this.stop(next === 'กระเป๋าเต็ม' ? 'full' : 'short');
    }
  }

  private gainXp(prof: ProfessionId, xp: number): void {
    const before = this.level(prof);
    this.state.xp[prof] = (this.state.xp[prof] ?? 0) + xp;
    const after = this.level(prof);
    if (after > before) {
      this.host.emit({ type: 'tradeLevel', prof, level: after });
      this.host.notice(`${PROFESSIONS[prof].name} ขึ้นขั้น ${after}!`);
      this.host.log(`${PROFESSIONS[prof].name} ขึ้นขั้น ${after}`);
    }
  }

  // ── goods ──

  /** Adds goods (up to a full stack); returns how many went in. */
  give(id: string, n: number): number {
    const have = this.has(id);
    const add = Math.max(0, Math.min(n, GOOD_STACK - have));
    if (add) this.state.goods[id] = have + add;
    return add;
  }

  /** Takes goods if there are enough. */
  private take(id: string, n: number): boolean {
    const have = this.has(id);
    if (have < n) return false;
    if (have === n) delete this.state.goods[id];
    else this.state.goods[id] = have - n;
    return true;
  }

  private use(id: string): void {
    const g = GOODS[id];
    const p = this.host.player;
    if (!g?.use || this.has(id) < 1 || p.dead) return;
    if (this.goodCooldown > 0) return this.host.notice(`รออีก ${Math.ceil(this.goodCooldown)} วินาที`);
    this.take(id, 1);
    this.goodCooldown = GOOD_COOLDOWN;
    if (g.use.hp) p.hp = Math.min(p.maxHp, p.hp + p.maxHp * g.use.hp);
    if (g.use.qi) p.qi = Math.min(p.maxQi, p.qi + p.maxQi * g.use.qi);
    if (g.use.status) this.host.addStatus(p, g.use.status, 1);
    if (g.use.train) this.host.trainBoost?.(g.use.train.bonus, g.use.train.seconds);
    // Healing during work is not a blow.
    if (this.job) this.job.hp = p.hp;
    this.host.emit({ type: 'usedGood', id: p.id, good: id });
    this.host.log(`ใช้「${g.name}」`);
    this.host.emit({ type: 'progressChanged' });
  }

  private buy(id: string, n: number): void {
    const g = GOODS[id];
    if (!g?.buy || !this.host.nearMerchant()) return;
    const pr = this.host.progress;
    const want = Math.max(1, Math.min(99, Math.floor(n) || 1));
    const can = Math.min(want, GOOD_STACK - this.has(id), Math.floor(pr.gold / g.buy));
    if (can <= 0) return this.host.notice(pr.gold < g.buy ? 'ตำลึงไม่พอ' : 'ถือได้ไม่เกินนี้แล้ว');
    pr.gold -= can * g.buy;
    this.give(id, can);
    this.host.notice(`ซื้อ「${g.name}」×${can} แล้ว`);
    this.host.emit({ type: 'progressChanged' });
  }

  private sell(id: string, n: number): void {
    const g = GOODS[id];
    if (!g || !this.host.nearMerchant()) return;
    const k = Math.min(this.has(id), Math.max(1, Math.floor(n) || 1));
    if (k <= 0) return;
    this.take(id, k);
    this.host.progress.gold += k * g.price;
    this.host.notice(`ขาย「${g.name}」×${k} ได้ ${k * g.price} ตำลึง`);
    this.host.emit({ type: 'progressChanged' });
  }
}
